import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from "@mui/material";
import { Accordion, AccordionSummary, AccordionDetails, CssBaseline, DialogActions, DialogContent, DialogTitle, Divider, Input, Modal, ModalDialog, Stack, ThemeProvider, Tooltip, Typography, IconButton } from "@mui/joy";
import Grid from '@mui/joy/Grid';
import Button from '@mui/joy/Button';
import { useNavigate } from 'react-router-dom'
import Snackbar from '@mui/joy/Snackbar';
import CustomTextField from "../../main/widgets/CustomTextField";
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { Breadcrumbs, Link } from "@mui/joy";
import { DataGrid } from "@mui/x-data-grid";
import NativeCheckbox from "../../main/components/NativeCheckbox";
import { dataGridNativeCheckboxSlots } from "../../main/components/dataGridNativeCheckboxSlots";
import LocationOptionList from "../../main/widgets/LocationOptionList";
import StatusOptionList from "../../main/widgets/StatusOptionList";
import TrackingEventTypeOptionList from "../../main/widgets/TrackingEventTypeOptionList";
import { receiveShipmentsRequest, scanUpdateShipment } from "../../main/API/shipment";
import { scanResolve } from "../../main/API/shipmentPiece";
import LaunchIcon from '@mui/icons-material/Launch';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import { MuiPhone } from "../../main/widgets/MuiPhone";
import PaymentMethodOptionList from "../../main/widgets/PaymentMethodOptionList";
import AccountOptionList from "../../main/widgets/AccountOptionList";
import { getPaymentStatusIconConfig } from "../../main/widgets/PaymentStatusOptionList";
import { addShipmentPayment } from "../../main/API/payment";
import { isPositivePaymentAmount } from "../../main/constants/paymentMethods";
import { readCustomer } from "../../main/API/customer";
import { theme } from "../../main/theme/constants";
import { DeleteForever, Edit, ExpandMore, KeyboardArrowRight, MoreVert, Menu, MenuItem, ListItemDecorator, ListDivider, VolumeUp, VolumeOff } from "@mui/icons-material";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { usePermissions } from "../../main/hooks/usePermissions";
import PermissionGate from "../../main/components/PermissionGate";
import { PERMISSIONS } from "../../main/context/AuthContext";
import useTrackingEventTypeOptions from "../../main/hooks/useTrackingEventTypeOptions";
import { labelForEventTypeCode } from "../../main/utils/trackingEventLabels";
import { getLocationDisplayName } from "../../main/utils/locationDisplayName";
import { getLocalizedUserFullName } from "../../main/utils/userDisplayName";
import { OmrAmount } from "../../main/components/OmrAmount";
import OmrSymbol from "../../main/components/OmrSymbol";

export default function ScanShipmentScreen({ formType, shipmentID }) {
    const { t, i18n } = useTranslation('ui');
    const { hasAnyRole, hasPermission } = usePermissions();
    const canOpenShipmentEdit = hasPermission(PERMISSIONS.SHIPMENTS_UPDATE);
    const canListLocations = hasPermission(PERMISSIONS.LOCATIONS_LIST);
    const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);
    const { options: eventTypeOptions } = useTrackingEventTypeOptions(canListEventTypes);
    // Collaborators and college users only see Update values (no Scan section)
    const isCollaboratorOrCollege = hasAnyRole(['collaborator', 'college']);



    //table payment status icon (colors from PaymentStatusOptionList)
    const paymentStatusIcon = useCallback((paymentStatus) => {
        const config = getPaymentStatusIconConfig(paymentStatus);
        return (
            <Tooltip title={config.title}>
                <MonetizationOnIcon
                    color={config.color === 'success' ? 'success' : undefined}
                    sx={config.color !== 'success' ? { color: config.color } : undefined}
                />
            </Tooltip>
        );
    }, []);

    //table formate time
    function formateTime(date) {
        var d = new Date(date);
        var time = d.toLocaleTimeString('en-US', { timeZone: 'Asia/Muscat', hour12: true, hour: 'numeric', minute: 'numeric' });

        var dateString = d.getDate() + "/" + (d.getMonth() + 1) + "/" + d.getFullYear() + " " + time;

        return dateString;

    }

    // Play short beep(s) using Web Audio (no sound files needed)
    function playBeep(frequencyHz, durationMs, startTime = 0) {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = frequencyHz;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0.15, startTime);
            gain.gain.exponentialRampToValueAtTime(0.01, startTime + durationMs / 1000);
            osc.start(startTime);
            osc.stop(startTime + durationMs / 1000);
            return startTime + durationMs / 1000;
        } catch (_) { return startTime; }
    }
    function playSuccessSound() {
        const t0 = 0;
        const t1 = playBeep(523, 80, t0);
        playBeep(659, 80, t1 + 0.05);
    }
    function playFailedSound() {
        const t0 = 0;
        const t1 = playBeep(200, 120, t0);
        playBeep(150, 180, t1 + 0.08);
    }



    const columns = useMemo(() => [
        { field: 'id', headerName: t('shipments.colId'), flex: 1, minWidth: 80 },
        {
            field: 'owner',
            headerName: t('shipments.colCustomer'),
            flex: 2,
            minWidth: 180,
            renderCell: (params) => {
                const owner = params.row.owner;
                if (owner && typeof owner === 'object') {
                    const name = getLocalizedUserFullName(owner, i18n.language);
                    return name || '—';
                }
                return t('scanShipments.unknownCustomer');
            }
        },
        { field: 'shipper_reference', headerName: t('shipments.colShipperRef'), flex: 2, minWidth: 150 },
        { field: 'tracking_number', headerName: t('shipments.colTracking'), flex: 2, minWidth: 150 },
        {
            field: 'shipment_unit_serial_number',
            headerName: t('shipments.colUnitSerial'),
            flex: 2,
            minWidth: 150,
            renderCell: (params) => (params.row.shipment_unit_serial_number != null && params.row.shipment_unit_serial_number !== '' ? params.row.shipment_unit_serial_number : '—'),
        },
        {
            field: 'price',
            headerName: t('shipments.colPrice'),
            renderHeader: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <span>{t('shipments.colPrice')}</span>
                    <OmrSymbol height={14} />
                </Box>
            ),
            flex: 2,
            minWidth: 120,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => <OmrAmount value={params.row.price} empty="—" />,
        },
        { field: 'payment_status', headerName: t('shipments.colPaymentStatus'), flex: 1.5, minWidth: 120, renderCell: (params) => paymentStatusIcon(params.row.payment_status != null ? params.row.payment_status : (params.row.paid ? 'paid' : 'unpaid')) },
        { field: 'status', headerName: t('shipments.colStatus'), flex: 2, minWidth: 120 },
        {
            field: 'event_type',
            headerName: t('scanShipments.colCurrentEventType'),
            flex: 2,
            minWidth: 160,
            renderCell: (params) => params.row.event_type
                ? labelForEventTypeCode(params.row.event_type, eventTypeOptions)
                : ""
        },
        { field: 'location', headerName: t('shipments.colLocation'), flex: 2, minWidth: 150, renderCell: (params) => (params.row.location ? getLocationDisplayName(params.row.location, i18n.language) : "") },
        { field: 'created_at', headerName: t('shipments.colCreatedAt'), flex: 2, minWidth: 150, renderCell: (params) => formateTime(params.row.created_at) },
        ...(canOpenShipmentEdit ? [{
            field: 'actions',
            headerName: t('shipments.colActions'),
            flex: 0,
            minWidth: 100,
            maxWidth: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Tooltip title={t('shipments.tooltipEditShipment')}>
                    <IconButton
                        size="sm"
                        variant="soft"
                        color="primary"
                        component="a"
                        href={`/Shipment/${params.row.id}/EditShipment`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Edit sx={{ fontSize: '1.125rem' }} />
                    </IconButton>
                </Tooltip>
            ),
        }] : []),
    ], [t, i18n.language, eventTypeOptions, paymentStatusIcon, canOpenShipmentEdit]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [responseMessage, setResponseMessage] = useState("");
    const [successNotification, setSuccessNotification] = useState(false);
    const [failedNotification, setFailedNotification] = useState(false);
    const [isUpdatingPaymentStatus, setIsUpdatingPaymentStatus] = useState(false);



    const [isLoading, setLoading] = useState(false);

    const [barcode, setBarcode] = useState("");
    const barcodeRef = useRef(null);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });

    useEffect(() => {
        if (barcodeRef.current) {
            barcodeRef.current.focus();
        }


    }, [isLoading]);

    //handle scanning button 
    const handleScanning = () => {
        if (barcodeRef.current) {
            barcodeRef.current.focus();
        }
        if (barcode) {
            updateShipment(barcode);
        }

    };

    //control values

    //Snackbar controller
    const [successUpdate, setSuccessUpdate] = useState(false);
    const [failedUpdate, setFailedUpdate] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    //Form Values
    const [currentLocation, setCurrentLocation] = useState(undefined);
    const [status, setStatus] = useState(undefined);
    const [currentEventTypeOpt, setCurrentEventTypeOpt] = useState(null);
    const [sendMessage, setSendMessage] = useState(false);
    const [scannedShipments, setScannedShipments] = useState([])
    const [shipmentsQueue, setShipmentsQueue] = useState([])
    const [soundMuted, setSoundMuted] = useState(false)

    // Allow scan when every shown field (location / status / event type) is set together, or all shown fields are empty.
    // Dimensions without list permission are omitted from this rule.
    const hasStatus = status != null && status !== "";
    const allSelected =
        (canListLocations ? currentLocation != null : true) &&
        hasStatus &&
        (canListEventTypes ? currentEventTypeOpt != null && currentEventTypeOpt.code : true);
    const noneSelected =
        (canListLocations ? currentLocation == null : true) &&
        !hasStatus &&
        (canListEventTypes ? currentEventTypeOpt == null || !currentEventTypeOpt.code : true);
    const canScan = allSelected || noneSelected;

    //Receive Shipments variables
    const [civilId, setCivilId] = useState(null);
    const [receiverName, setReceiverName] = useState(null);
    const [receiverPhoneNumber, setReceiverPhoneNumber] = useState(null);
    const [sendShipmentsMessages, setSendShipmentsMessages] = useState(true);
    const [receiveShipmentStatus, setReceiveShipmentStatus] = useState('delivered');
    const [receiveShipmentEventType, setReceiveShipmentEventType] = useState(null);
    const [isReceiveShipmentLoading, setIsReceiveShipmentLoading] = useState(false);
    const [openReceiveShipmentsAlert, setOpenReceiveShipmentsAlert] = useState(false);


    //Pay Shipments variables
    const [isPayShipmentLoading, setIsPayShipmentLoading] = useState(false);
    const [openPayShipmentsAlert, setOpenPayShipmentsAlert] = useState(false);
    const [totalAmount, setTotalAmount] = useState("");
    const [transactionReference, setTransactionReference] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [account, setAccount] = useState(null);
    // Get today's date in YYYY-MM-DD format for date input
    const getTodayDate = useMemo(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);
    const [transactionDate, setTransactionDate] = useState(getTodayDate);

    const handlePaymentShipments = async () => {
        if (!hasPermission(PERMISSIONS.PAYMENTS_ADD_SHIPMENT)) return;
        setIsPayShipmentLoading(true);

        // Validation: Check if all selected shipments are invoiced
        const selectedShipments = scannedShipments.filter(row => selectionModel.includes(row.id));
        const uninvoicedShipments = selectedShipments.filter(row => !row.invoiced);
        if (uninvoicedShipments.length > 0) {
            setResponseMessage(t('shipments.paymentNotInvoiced'));
            setFailedNotification(true);
            setIsPayShipmentLoading(false);
            setOpenPayShipmentsAlert(false);
            return;
        }

        // Validation: Check if required fields are filled
        if (!account) {
            setResponseMessage(t('shipments.accountRequired'));
            setFailedNotification(true);
            setIsPayShipmentLoading(false);
            return;
        }
        if (!transactionDate) {
            setResponseMessage(t('shipments.transactionDateRequired'));
            setFailedNotification(true);
            setIsPayShipmentLoading(false);
            return;
        }
        if (!isPositivePaymentAmount(totalAmount)) {
            setResponseMessage(t('shipments.paymentAmountPositive'));
            setFailedNotification(true);
            setIsPayShipmentLoading(false);
            return;
        }

        // Collect data
        const payload = {
            'shipment_ids': selectionModel,
            'payment_data': {
                'total_amount': totalAmount,
                'transaction_reference': transactionReference,
                'payment_method': paymentMethod,
                'account': account.id,
                'transaction_date': transactionDate
            }

        };

        try {
            await addShipmentPayment(payload).then(
                async actualData => {
                    if (actualData.status === 200) {
                        setResponseMessage(t('shipments.paymentRegistered'));
                        setSuccessNotification(true);
                        setOpenPayShipmentsAlert(false);
                        resetFields();
                    } else {
                        setResponseMessage(t('shipments.paymentProcessError'));
                        setFailedNotification(true);
                    }
                    setIsPayShipmentLoading(false);

                });


        } catch (error) {
            setIsPayShipmentLoading(false);
            setResponseMessage(error.message);
            setFailedNotification(true);
            console.error('Error processing the payment:', error);
        }
    };



    // Update shipment via PUT shipment/<id>/scan_update/ (optional: location, status, event_type, send_whatsapp)
    const updateShipment = async (event) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        if (!hasPermission(PERMISSIONS.SHIPMENTS_SCAN_UPDATE)) return;
        if (!canScan) {
            setFailedUpdate(true);
            setErrorMessage(t('scanShipments.errorSelectThreeOrAllEmpty'));
            return;
        }
        const barcodeTemp = barcode;
        setShipmentsQueue(prev => [...prev, barcodeTemp]);
        setBarcode("");
        setLoading(true);

        const requestBody = {};
        if (currentLocation != null) requestBody.location = currentLocation.id;
        if (status != null && status !== "") requestBody.status = status;
        if (currentEventTypeOpt != null && currentEventTypeOpt.code) {
            requestBody.event_type = currentEventTypeOpt.code;
        }
        if (sendMessage) requestBody.send_whatsapp = true;

        try {
            const response = await scanUpdateShipment(barcodeTemp, requestBody);
            if (response.status === 200) {
                let shipment = response.data;
                const isReplay = Boolean(shipment && shipment.idempotent_replay);
                const owner = shipment.owner;
                const hasOwnerName = owner && typeof owner === 'object' && (
                    owner.first_name != null || owner.last_name != null
                    || owner.first_name_ar != null || owner.last_name_ar != null
                );
                if (!hasOwnerName && owner != null) {
                    const ownerId = typeof owner === 'number' ? owner : owner.id;
                    if (ownerId != null) {
                        try {
                            const customerRes = await readCustomer(ownerId);
                            if (customerRes && customerRes.status === 200 && customerRes.data) {
                                const d = customerRes.data;
                                shipment = {
                                    ...shipment,
                                    owner: {
                                        id: d.id,
                                        first_name: d.first_name,
                                        last_name: d.last_name,
                                        first_name_ar: d.first_name_ar,
                                        last_name_ar: d.last_name_ar,
                                    },
                                };
                            }
                        } catch (_) { /* keep shipment.owner as ID if fetch fails */ }
                    }
                }
                setScannedShipments(prev => {
                    const updated = prev.filter(s => s.id !== shipment.id);
                    return [shipment, ...updated];
                });
                // Idempotent replay: server already applied this client_event_id —
                // skip the success beep so retries don't reward the user twice.
                if (isReplay) {
                    setFailedUpdate(true);
                    setErrorMessage(t('scanPiece.result.alreadyScanned'));
                } else {
                    setSuccessUpdate(true);
                    if (!soundMuted) playSuccessSound();
                }
            } else {
                const msg = (response.data && (response.data.message || response.data.detail)) || `Error ${response.status}`;
                setFailedUpdate(true);
                setErrorMessage(msg);
                if (!soundMuted) playFailedSound();
            }
        } catch (error) {
            const resData = error.response && error.response.data;
            // If the scanned code looks like a piece barcode (not a shipment id /
            // tracking number), the API returns 404 here. Try the universal
            // resolver and surface a hint pointing to the Scan Piece screen so
            // the user isn't stuck (FRONTEND_INTEGRATION_GUIDE §8 + FAQ).
            if (error && error.response && error.response.status === 404) {
                try {
                    const resolved = await scanResolve(barcodeTemp);
                    const kind = resolved && resolved.data && resolved.data.kind;
                    if (kind === 'piece') {
                        setFailedUpdate(true);
                        setErrorMessage(t('scanPiece.result.tryUniversal'));
                        if (!soundMuted) playFailedSound();
                        return;
                    }
                } catch (_) { /* ignore — fall through to generic error */ }
            }
            const msg = (resData && (resData.message || (typeof resData === 'object' ? JSON.stringify(resData) : resData))) || error.message;
            setFailedUpdate(true);
            setErrorMessage(msg || t('scanShipments.genericUpdateError'));
            if (!soundMuted) playFailedSound();
        } finally {
            setLoading(false);
            setShipmentsQueue(prev => prev.filter(id => id !== barcodeTemp));
        }
    };

    const openReceiveShipmentsModal = () => {
        const shipment = scannedShipments.find((row) => selectionModel.includes(row.id));
        setReceiveShipmentStatus('delivered');
        const eventTypeCode =
            shipment?.event_type != null && String(shipment.event_type).trim() !== ''
                ? String(shipment.event_type).trim()
                : 'delivered';
        setReceiveShipmentEventType({
            code: eventTypeCode,
            name: shipment?.event_type_name ?? eventTypeCode,
        });
        setOpenReceiveShipmentsAlert(true);
    };

    const receiveShipments = async () => {
        if (!hasPermission(PERMISSIONS.SHIPMENTS_RECEIVE)) return;
        if (!receiveShipmentEventType?.code) {
            setResponseMessage(t('trackingEvent.errEventTypeRequired'));
            setFailedNotification(true);
            return;
        }
        setIsReceiveShipmentLoading(true);
        // Collect data
        const payload = {
            'shipments_ids': selectionModel,
            'receiver_civil_id': civilId != null ? civilId : "",
            'receiver_phone_number': receiverPhoneNumber != null ? receiverPhoneNumber : "",
            'receiver_name': receiverName != null ? receiverName : "",
            'status': receiveShipmentStatus,
            'event_type': receiveShipmentEventType.code,
            'send_shipments_messages': sendShipmentsMessages
        };
        console.log(payload);

        try {
            var response = await receiveShipmentsRequest(payload).then(async actualData => {
                //control success
                if (actualData.status == 200) {
                    setResponseMessage(t('shipments.receiverRegistered'));
                    //send notification
                    setSuccessNotification(true);
                    //fetch invoices again
                }
                //control other responses
                else {
                    setResponseMessage(t('shipments.errorInvoiceReceiver'));
                    //send notification
                    setFailedNotification(true);
                }
                //stop loading 
                setIsUpdatingPaymentStatus(false);
            }).catch((err) => {

                //stop loading 
                setIsUpdatingPaymentStatus(false);
                console.log(err.message);
            });

            if (response.ok) {
                const result = await response.json();

                console.log('Record saved successfully:', result);

                // Reset variables
                resetFields();
                setOpenReceiveShipmentsAlert(false);
            } else {
                console.error('Failed to save the record:', response.statusText);
            }
            setIsReceiveShipmentLoading(false);
        } catch (error) {
            setIsReceiveShipmentLoading(false);
            console.error('Error saving the record:', error);
        }
    };

    // Function to reset form fields of receiver info and pay shipment
    const resetFields = () => {
        setCivilId('');
        setReceiverName('');
        setReceiverPhoneNumber('+968')
        setReceiveShipmentStatus('delivered');
        setReceiveShipmentEventType(null);
        setTotalAmount("");
        setTransactionReference("");
        setPaymentMethod("");
        setAccount(null);
        setTransactionDate(getTodayDate);
    };


    function resetVariables() {
        setStatus(null);
        setCurrentLocation(null);
        setCurrentEventTypeOpt(null);
        setBarcode("");
        setSendMessage(false);
    }

    const canForceWhatsapp = hasPermission(PERMISSIONS.WHATSAPP_SEND_SHIPMENT_NOTIFICATION);
    const whatsAppScanUpdateLabel = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <WhatsAppIcon sx={{ fontSize: '1.125rem', color: '#25D366', flexShrink: 0 }} aria-hidden />
            <span>{t('scanShipments.forceSendMessage')}</span>
        </Box>
    );
    const whatsAppReceiveShipmentsLabel = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <WhatsAppIcon sx={{ fontSize: '1.125rem', color: '#25D366', flexShrink: 0 }} aria-hidden />
            <span>{t('shipments.sendShipmentsMsgCustomer')}</span>
        </Box>
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <form onSubmit={updateShipment}>
                <Box sx={{ display: 'flex' }} m={3}>
                    <Box width={"100%"}>
                        <Box>
                            <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                                {[t('scanShipments.breadcrumb1'), t('scanShipments.breadcrumb2')].map((item) => (
                                    <Link key={item} color="neutral" href="/shipments">
                                        {item}
                                    </Link>
                                ))}
                            </Breadcrumbs>
                            <Typography textAlign={"start"} level="h1">{t('scanShipments.pageTitle')}</Typography>
                        </Box>

                        {/* Update values (collapsible only) */}
                        <Accordion defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="update-values-content"
                                id="update-values-header"
                                sx={{ display: 'flex', alignItems: 'center' }}
                            >
                                <Typography level="h3">{t('scanShipments.updateValues')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                                    {canListLocations && (
                                    <Grid item xs={12} sm={6} md={3} lg={3}>
                                        <LocationOptionList
                                            key={currentLocation ? `location-${currentLocation.id}` : 'location-cleared'}
                                            label={t('scanShipments.currentLocation')}
                                            required={false}
                                            value={currentLocation}
                                            defaultValue={currentLocation}
                                            onChange={(event, value) => setCurrentLocation(value)}
                                        />
                                    </Grid>
                                    )}
                                    <Grid item xs={12} sm={6} md={3} lg={3}>
                                        <StatusOptionList
                                            required={false}
                                            status={status}
                                            setStatus={(newValue) => setStatus(newValue)}
                                            showAll={false}
                                            disabled={false}
                                        />
                                    </Grid>
                                    {canListEventTypes && (
                                    <Grid item xs={12} sm={6} md={3} lg={3}>
                                        <TrackingEventTypeOptionList
                                            label={t('scanShipments.currentEventType')}
                                            required={false}
                                            defaultValue={currentEventTypeOpt}
                                            onChange={(_, v) => setCurrentEventTypeOpt(v ?? null)}
                                        />
                                    </Grid>
                                    )}
                                    {canForceWhatsapp && (
                                    <Grid item xs={12} sm={6} md={3} lg={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Tooltip title={t('scanShipments.forceSendMessageTooltip')}>
                                            <Box>
                                                <NativeCheckbox
                                                    checked={sendMessage}
                                                    onChange={(event) => setSendMessage(event.target.checked)}
                                                    label={whatsAppScanUpdateLabel}
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Grid>
                                    )}
                                    <Grid item xs={12} sm={6} md={3} lg={3} sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>
                                        <Button variant="plain" color="neutral" onClick={resetVariables}>
                                            {t('scanShipments.clear')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        {/* Scan section (always visible, not collapsible; hidden for collaborators/college) */}
                        {!isCollaboratorOrCollege && (
                            <Box sx={{ mt: 2 }}>
                                <Typography level="title-md" sx={{ mb: 1.5, fontWeight: 600 }}>{t('scanShipments.scanSection')}</Typography>
                                <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                                    <Grid item xs={12} sm={6} md={3} lg={3}>
                                        <CustomTextField
                                            slotProps={{
                                                input: {
                                                    min: 0,
                                                    max: 999999999999999,
                                                    step: 1,
                                                },
                                            }}
                                            inputRef={barcodeRef}
                                            onChange={(event) => {
                                                setBarcode(event.target.value);
                                            }}
                                            required={true}
                                            type="number"
                                            value={barcode}
                                            label={t('scanShipments.barcodeLabel')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3} lg={3} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 1 }}>
                                        <PermissionGate permission={PERMISSIONS.SHIPMENTS_SCAN_UPDATE}>
                                            <Tooltip title={canScan ? t('scanShipments.tooltipScan') : t('scanShipments.tooltipScanRule')}>
                                                <span>
                                                    <Button type="submit" loading={shipmentsQueue.length != 0} onClick={handleScanning} disabled={!canScan}>
                                                        {t('scanShipments.scanButton')}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </PermissionGate>
                                        <Tooltip title={soundMuted ? t('scanShipments.tooltipUnmute') : t('scanShipments.tooltipMute')}>
                                            <IconButton
                                                variant="plain"
                                                color="neutral"
                                                size="md"
                                                onClick={() => setSoundMuted(prev => !prev)}
                                                aria-label={soundMuted ? t('scanShipments.ariaUnmute') : t('scanShipments.ariaMute')}
                                            >
                                                {soundMuted ? <VolumeOff /> : <VolumeUp />}
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Actions icons only */}
                        <Grid mt={2} container spacing={2} justifyContent="flex-end">
                            <Grid
                                container
                                spacing={1}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-start'
                                }}
                            >
                                <PermissionGate permission={PERMISSIONS.SHIPMENTS_RECEIVE}>
                                    <Grid item>
                                        <Tooltip title={t('scanShipments.tooltipReceive')}>
                                            <IconButton
                                                onClick={openReceiveShipmentsModal}
                                                disabled={selectionModel.length === 0}
                                                loading={isReceiveShipmentLoading}
                                                color="primary"
                                                size="large"
                                                sx={{
                                                    mr: { xs: 0, md: 1 },
                                                    width: { xs: '100%', md: 'auto' },
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: '1.75rem'
                                                    }
                                                }}
                                            >
                                                <LaunchIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </PermissionGate>
                                <PermissionGate permission={PERMISSIONS.PAYMENTS_ADD_SHIPMENT}>
                                    <Grid item>
                                        <Tooltip title={
                                            selectionModel.length <= 0
                                                ? t('shipments.tooltipPaySelect')
                                                : scannedShipments.filter(row => selectionModel.includes(row.id)).some(row => !row.invoiced)
                                                    ? t('shipments.tooltipPayInvoiceFirst')
                                                    : t('shipments.tooltipMarkPaid')
                                        }>
                                            <span>
                                                <IconButton
                                                    onClick={() => setOpenPayShipmentsAlert(true)}
                                                    disabled={selectionModel.length <= 0 || scannedShipments.filter(row => selectionModel.includes(row.id)).some(row => !row.invoiced)}
                                                    loading={isPayShipmentLoading}
                                                    color="success"
                                                    size="large"
                                                    sx={{
                                                        mr: { xs: 0, md: 1 },
                                                        width: { xs: '100%', md: 'auto' },
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: '1.75rem'
                                                        }
                                                    }}
                                                >
                                                    <PriceCheckIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                </PermissionGate>
                            </Grid>
                        </Grid>

                        <Box>
                            <DataGrid
                                slots={dataGridNativeCheckboxSlots}
                                checkboxSelection
                                onRowSelectionModelChange={(newSelection) => {
                                    setSelectionModel(newSelection);
                                }}
                                selectionModel={selectionModel}
                                rowCount={scannedShipments.length}
                                rows={scannedShipments || []}
                                columns={columns}
                                loading={shipmentsQueue.length != 0}
                                initialState={{ pagination: { paginationModel } }}
                                pageSizeOptions={[25, 50, 100]}
                                hideFooterSelectedRowCount
                                getRowHeight={() => 'auto'}
                                onRowClick={canOpenShipmentEdit
                                    ? (params) => window.open(`/Shipment/${params.row.id}/EditShipment`, '_blank')
                                    : undefined}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                    '& .MuiDataGrid-root': {
                                        border: 'none',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f5f5f5',
                                        borderBottom: '2px solid #e0e0e0',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#333',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#333',
                                        },
                                    },
                                    '& .MuiDataGrid-cell': {
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        lineHeight: '1.5',
                                        padding: '12px 16px',
                                        fontSize: '14px',
                                        color: '#555',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                        borderBottom: '1px solid #f0f0f0',
                                    },
                                    '& .MuiDataGrid-row': {
                                        ...(canOpenShipmentEdit && {
                                            '&:hover': {
                                                backgroundColor: '#f8f9fa',
                                                cursor: 'pointer',
                                            },
                                        }),
                                        '&.Mui-selected': {
                                            backgroundColor: '#e3f2fd',
                                            '&:hover': {
                                                backgroundColor: '#bbdefb',
                                            },
                                        },
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        borderTop: '2px solid #e0e0e0',
                                        backgroundColor: '#fafafa',
                                        fontSize: '14px',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                    },
                                    '& .MuiDataGrid-toolbarContainer': {
                                        padding: '12px',
                                        backgroundColor: '#fafafa',
                                        borderBottom: '1px solid #e0e0e0',
                                    },
                                    '@media (max-width: 600px)': {
                                        '& .MuiDataGrid-toolbarContainer': {
                                            flexDirection: 'column',
                                            gap: 1,
                                        },
                                        '& .MuiDataGrid-footer': {
                                            flexDirection: 'column',
                                            gap: 1,
                                        },
                                        '& .MuiDataGrid-cell': {
                                            minHeight: '60px',
                                            alignItems: 'center',
                                            padding: '8px',
                                            fontSize: '13px',
                                        },
                                        '& .MuiDataGrid-columnHeaders': {
                                            fontSize: '13px',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Receive Shipments Alert */}
                        <Modal open={openReceiveShipmentsAlert} onClose={() => setOpenReceiveShipmentsAlert(false)}>
                            <ModalDialog variant="outlined" role="alertdialog">
                                <DialogTitle>
                                    <LaunchIcon />
                                    {t('shipments.receiveModalTitle')}
                                </DialogTitle>
                                <Divider />
                                <Grid container spacing={1} sx={{ flexGrow: 1 }}>
                                    <CustomTextField required type="number" onChange={(e) => setCivilId(e.target.value)} value={civilId} label={t('shipments.civilId')} xs={12} />
                                    <CustomTextField required onChange={(event) => setReceiverName(event.target.value)} value={receiverName} label={t('shipments.receiverName')} xs={12} />

                                    <Grid container spacing={1} sx={{ flexGrow: 1 }}>
                                        <MuiPhone value={"+968"} onChange={(value => setReceiverPhoneNumber(value))}></MuiPhone>
                                    </Grid>

                                    <Grid xs={12}>
                                        <StatusOptionList
                                            label={t('shipments.shipmentsStatus')}
                                            required={true}
                                            status={receiveShipmentStatus}
                                            setStatus={(newValue) => setReceiveShipmentStatus(newValue)}
                                            showAll={false}
                                            disabled={false}
                                        />
                                    </Grid>
                                    <Grid xs={12}>
                                        <TrackingEventTypeOptionList
                                            key={
                                                receiveShipmentEventType
                                                    ? `receive-event-type-${receiveShipmentEventType.code}`
                                                    : 'receive-event-type-cleared'
                                            }
                                            label={t('widgets.eventType')}
                                            required={true}
                                            defaultValue={receiveShipmentEventType}
                                            onChange={(_, value) => setReceiveShipmentEventType(value ?? null)}
                                            disabled={isReceiveShipmentLoading}
                                        />
                                    </Grid>
                                    <Grid xs={12}>
                                        <DialogContent>
                                            <NativeCheckbox checked={sendShipmentsMessages} onChange={(event) => setSendShipmentsMessages(event.target.checked)} label={whatsAppReceiveShipmentsLabel} />
                                        </DialogContent>
                                    </Grid>

                                    <DialogActions>
                                        <Button variant="solid" color="success" onClick={() => {
                                            receiveShipments();
                                            setOpenReceiveShipmentsAlert(false);
                                            resetFields();
                                        }}>
                                            {t('actions.submit')}
                                        </Button>
                                        <Button variant="plain" color="neutral" onClick={() => {
                                            setOpenReceiveShipmentsAlert(false);
                                            resetFields();
                                        }}>
                                            {t('actions.cancel')}
                                        </Button>
                                    </DialogActions>
                                </Grid>
                            </ModalDialog>
                        </Modal>

                        {/* Pay Shipments Alert */}
                        <Modal open={openPayShipmentsAlert} onClose={() => setOpenPayShipmentsAlert(false)}>
                            <ModalDialog variant="outlined" role="alertdialog">
                                <DialogTitle>
                                    <PriceCheckIcon />
                                    {t('shipments.payModalTitle')}
                                </DialogTitle>
                                <Divider />
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    handlePaymentShipments();
                                }}>
                                    <Grid container spacing={1} sx={{ flexGrow: 1 }}>
                                        <CustomTextField required slotProps={{
                                            input: {
                                                min: 0.001,
                                                max: 999999999999999,
                                                step: 0.001,
                                            },
                                        }} type="number" onChange={(event) => setTotalAmount(event.target.value)} value={totalAmount} label={t('shipments.amount')} endDecorator={<OmrSymbol height={16} />} xs={12} ></CustomTextField>
                                        <CustomTextField required onChange={(event) => setTransactionReference(event.target.value)} value={transactionReference} label={t('shipments.transactionReference')} xs={12} />
                                        <Grid xs={12}>
                                            <PaymentMethodOptionList
                                                label={t('widgets.paymentMethod')}
                                                required={true}
                                                currentPaymentMethod={paymentMethod}
                                                setCurrentPaymentMethod={(newValue) => setPaymentMethod(newValue)}
                                                showAll={false}
                                                disabled={false}
                                            />
                                        </Grid>
                                        <Grid xs={12}>
                                            <AccountOptionList
                                                label={t('widgets.account')}
                                                required={true}
                                                currentAccount={account}
                                                setCurrentAccount={(newValue) => setAccount(newValue)}
                                                showActiveOnly={true}
                                                disabled={false}
                                            />
                                        </Grid>
                                        <Grid xs={12}>
                                            <Box sx={{ marginBottom: 2 }}>
                                                <Typography
                                                    level="body-sm"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#555',
                                                        marginBottom: '6px',
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    {t('shipments.transactionDate')} *
                                                </Typography>
                                                <Input
                                                    type="date"
                                                    value={transactionDate}
                                                    onChange={(e) => setTransactionDate(e.target.value)}
                                                    required
                                                    sx={{
                                                        backgroundColor: '#f7f9fc',
                                                        borderRadius: '8px',
                                                        padding: '8px',
                                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                        '&:hover': {
                                                            borderColor: '#5f09c2',
                                                        },
                                                        '&:focus': {
                                                            borderColor: '#5f09c2',
                                                            outline: 'none',
                                                            boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Grid>

                                        <DialogActions>
                                            <Button loading={isPayShipmentLoading} type="submit" variant="solid" color="success">
                                                {t('actions.submit')}
                                            </Button>
                                            <Button variant="plain" color="neutral" onClick={() => {
                                                setOpenPayShipmentsAlert(false);
                                                resetFields();
                                            }}>
                                                {t('actions.cancel')}
                                            </Button>
                                        </DialogActions>
                                    </Grid>
                                </form>
                            </ModalDialog>
                        </Modal>

                        {/* Notifications */}
                        <Snackbar
                            autoHideDuration={3000}
                            open={successUpdate}
                            variant={"soft"}
                            color={"success"}
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setSuccessUpdate(false);
                            }}
                        >
                            {t('shipmentForm.snackbarUpdated')}
                        </Snackbar>
                        <Snackbar
                            autoHideDuration={3000}
                            open={failedUpdate}
                            variant={"soft"}
                            color={"danger"}
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setFailedUpdate(false);
                            }}
                        >
                            {t('shipmentForm.snackbarUpdateFailed', {
                                detail:
                                    typeof errorMessage === 'string'
                                        ? errorMessage
                                        : errorMessage?.message || String(errorMessage ?? ''),
                            })}
                        </Snackbar>

                        {/* Generate Invoice Notification */}
                        <Snackbar
                            autoHideDuration={3000}
                            open={successNotification}
                            variant={"soft"}
                            color={"success"}
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setSuccessNotification(false);
                            }}
                        >
                            {responseMessage}
                        </Snackbar>
                        <Snackbar
                            autoHideDuration={3000}
                            open={failedNotification}
                            variant={"soft"}
                            color={"danger"}
                            onClose={(event, reason) => {
                                if (reason === 'clickaway') return;
                                setFailedNotification(false);
                            }}
                        >
                            {responseMessage}
                        </Snackbar>
                    </Box>
                </Box>
            </form>
        </ThemeProvider>
    );
}
